import {Controller} from '../Controller';
import {tAsString} from '../../common/helpers/react/text/tAsString';
import {Bound} from '../../common/decorators/methods/Bound';
import {Throttled} from '../../common/decorators/methods/Throttled';
import {BehaviorSubject, Observable} from 'rxjs';
import {SearchType} from '../../common/model/types/assignees/SearchType';
import {asGroupParticipantType} from '../../common/helpers/converters/asGroupParticipantType';

export class AgentsController extends Controller {
    #searchType = new BehaviorSubject<string>(SearchType.AGENT_NAME);
    #searchPattern = new BehaviorSubject<string>('');
    #toAddAssignees = new BehaviorSubject<string[] | undefined>([]);
    #lockChanges = new BehaviorSubject<number>(0);

    getSearchType(): Observable<string> {
        return this.#searchType;
    }

    setSearchType(type: string) {
        this.#searchType.next(type);
    }

    getSearchPattern(): Observable<string> {
        return this.#searchPattern;
    }

    setSearchPattern(pattern: string) {
        this.#searchPattern.next(pattern);
    }

    getToAddAssignees(): Observable<string[] | undefined> {
        return this.#toAddAssignees;
    }

    setToAddAssignees(assignees: string[] | undefined) {
        this.#toAddAssignees.next(assignees);
    }

    async assignAgentsToGroup$(groupId: number, agentIds: string[]) {
        try {
            await this.getBusyFlag().run(async () => {
                await this.services.agents.assignAgents(groupId, agentIds);
            }, tAsString('UPDATING_GROUP'));
        } finally {
            this.controllers.groups.refreshAllGroups().andWeAreDone();
            this.refreshAllAgents(this.#searchType.getValue()).andWeAreDone();
        }
    }

    async lockAgents$(agentIds: string[], lock: boolean) {
        try {
            await this.getBusyFlag().run(async () => {
                await this.services.agents.lockAgents(agentIds, lock);
            }, tAsString(lock ? 'LOCKING_AGENTS' : 'UNLOCKING_AGENTS'));

            this.#lockChanges.next(this.#lockChanges.value + 1);
        } finally {
            await this.refreshAllAgents(this.#searchType.getValue());
        }
    }

    getLockChanges$(): Observable<number> {
        return this.#lockChanges;
    }

    useForceUpdate(): unknown {
        return this.getLockChanges$().useState();
    }

    @Bound()
    @Throttled()
    async refreshAllAgents(type: string = SearchType.AGENT_NAME) {
        await this.getBusyFlag().run(async () => {
            await this.services.groups.searchAssignees({
                searchCriteria: {
                    type: asGroupParticipantType(type),
                    pattern: '*'
                }
            });
        }, tAsString('REFRESHING_AGENTS'));
    }
}